import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { HttpClient } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { catchError, interval, Observable, of, Subscription, throwError } from 'rxjs';
import { BusinessDomainService } from '../../business-domain/business-domain.service';
import { BusinessDomain } from '../../common/models/business-domain.model';
import { ConfigService } from '../../config/config.service';
import { ConnectionAutomationService } from '../connection-automation.service';
import { ConnectionPollingService } from '../connection-polling.service';
import { ConnectionAuthType } from '../models/connection-auth-types.model';
import { ConnectionType, connectionTypes } from '../models/connection-types.model';
import { CreateCollibraConnectionForm } from '../models/create-collibra-connection-request.model';
import { datalakeContainerOptions, datalakeStorageAccountOptions } from '../models/datalake-options.model';
import { ConnectionState } from '../models/get-connection-response.model';
import {
    containerNameValidator,
    dfsDomainValidator,
    jdbcUrlCredentialsValidator,
    storageAccountValidator,
} from './connection-form.validators';
import {
    createConnectionHost,
    createDatabricksConnectionRequest,
    createDataLakeConnectionRequest,
    createSqlConnectionRequest,
} from './connection-request.factory';
import { ConnectionResultDetails, ConnectionResultModalComponent } from './connection-result-modal.component';

import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { filter, finalize, map, startWith, switchMap, tap } from 'rxjs/operators';

type ConnectionAuthTypeOption = {
    connectionAuthType: ConnectionAuthType;
    disabled: boolean;
};

@Component({
    selector: 'app-create-connection',

    imports: [
        ReactiveFormsModule,
        CommonModule,
        MatButtonModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatProgressSpinnerModule,
        MatIconModule,
        MatFormFieldModule,
        MatExpansionModule,
        MatSlideToggleModule,
    ],
    templateUrl: './create-connection.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateConnectionComponent implements OnInit, OnDestroy {
    protected createConnectionForm: FormGroup<CreateCollibraConnectionForm>;

    protected connectionTypes = connectionTypes;
    protected connectionAuthTypeOptions$!: Observable<ConnectionAuthTypeOption[]>;
    protected datalakeStorageAccountOptions = datalakeStorageAccountOptions;
    protected datalakeContainerOptions = datalakeContainerOptions;
    protected businessDomains$!: Observable<BusinessDomain[]>;
    private readonly businessDomains = signal<BusinessDomain[]>([]);
    protected isCreatingConnection = signal(false);
    protected provisioningState = signal<ConnectionState | null>(null);
    protected provisioningElapsedSeconds = signal(0);
    protected provisioningMessage = signal<string | null>(null);
    protected connectionResult = signal<ConnectionResultDetails | null>(null);

    private connectionCreationSubscription?: Subscription;
    private elapsedTimeSubscription?: Subscription;
    private connectionResultModalRef?: NgbModalRef;
    private resultModalClosed = false;

    protected showInputForSQLPassword = signal(false);
    protected showInputForClientSecret = signal(false);
    protected hideSQLPassword = signal(true);
    protected hideClientSecret = signal(true);
    protected useBringYourOwnSP = signal(false);

    public constructor(
        private readonly fb: FormBuilder,
        private readonly businessDomainService: BusinessDomainService,
        private readonly connectionAutomationService: ConnectionAutomationService,
        private readonly connectionPollingService: ConnectionPollingService,
        private readonly route: ActivatedRoute,
        private readonly modalService: NgbModal,
        private readonly toastrService: ToastrService,
        private http: HttpClient,
        private configService: ConfigService,
    ) {
        this.createConnectionForm = fb.nonNullable.group({
            connectionAlias: fb.nonNullable.control<string>('', [Validators.required]),
            businessDomainId: fb.nonNullable.control<string>('', [Validators.required]),
            connectionType: fb.nonNullable.control<ConnectionType>('DataLake', [Validators.required]),
            connectionAuthType: fb.nonNullable.control<ConnectionAuthType>('ServicePrincipal', [Validators.required]),

            // DataLake path fields (split into three components)
            container: fb.nonNullable.control<string>('', [Validators.required, containerNameValidator()]),
            datalake: fb.nonNullable.control<string>('', [Validators.required, storageAccountValidator()]),
            dfsDomain: fb.nonNullable.control<string>({ value: 'dfs.core.windows.net', disabled: true }, [
                Validators.required,
                dfsDomainValidator(),
            ]),

            clientId: fb.nonNullable.control<string>('', [Validators.required]),
            clientSecret: fb.nonNullable.control<string>('', [Validators.required]),
            useBringYourOwnSP: fb.nonNullable.control<boolean>(false),

            refreshSecrets: fb.nonNullable.control<boolean>(false),

            host: fb.nonNullable.control<string>(''),
            username: fb.nonNullable.control<string>(''),
            password: fb.nonNullable.control<string>(''),
            databaseName: fb.nonNullable.control<string>(''),
            port: fb.nonNullable.control<number | null>(null),
            isPushdown: fb.nonNullable.control<boolean>(false),
            isUnityCatalog: fb.nonNullable.control<boolean>(false),
            catalog: fb.nonNullable.control<string>({ value: '', disabled: true }, [Validators.required]),
        });

        this.createConnectionForm.get('isUnityCatalog')?.valueChanges.subscribe(() => this.updateCatalogState());

        this.createConnectionForm.get('connectionType')?.valueChanges.subscribe((type) => {
            if (type === 'SQLServer') {
                this.setSqlValidators();
            } else if (type === 'DataLake') {
                this.setDataLakeValidators();
            } else if (type === 'Databricks') {
                this.setDatabricksValidators();
            }
        });

        const initialType = this.createConnectionForm.get('connectionType')?.value ?? 'DataLake';
        if (initialType === 'SQLServer') {
            this.setSqlValidators();
        } else if (initialType === 'DataLake') {
            this.setDataLakeValidators();
        }

        // Track the toggle state with a signal
        this.createConnectionForm.get('useBringYourOwnSP')?.valueChanges.subscribe((value) => {
            this.useBringYourOwnSP.set(value || false);

            // Enable/disable form controls based on toggle state
            const clientIdControl = this.createConnectionForm.get('clientId');
            const clientSecretControl = this.createConnectionForm.get('clientSecret');

            if (value) {
                clientIdControl?.enable();
                clientSecretControl?.enable();
            } else {
                clientIdControl?.disable();
                clientSecretControl?.disable();
            }
        });

        // Set initial state
        const initialToggleValue = this.createConnectionForm.get('useBringYourOwnSP')?.value;
        if (!initialToggleValue) {
            this.createConnectionForm.get('clientId')?.disable();
            this.createConnectionForm.get('clientSecret')?.disable();
        }
    }

    protected hasFieldError(fieldName: string, errorType: string = 'required'): boolean {
        const control = this.createConnectionForm.get(fieldName);
        return !!(control?.hasError(errorType) && control?.touched);
    }

    protected isKnownDatalakeStorageAccount(value: string | undefined): boolean {
        return value ? (datalakeStorageAccountOptions as readonly string[]).includes(value) : false;
    }

    protected isKnownDatalakeContainer(value: string | undefined): boolean {
        return value ? (datalakeContainerOptions as readonly string[]).includes(value) : false;
    }

    public ngOnInit(): void {
        this.connectionAuthTypeOptions$ = this.createConnectionForm.controls.connectionType.valueChanges.pipe(
            startWith('DataLake' as ConnectionType),
            map(() => [
                { connectionAuthType: 'ServicePrincipal', disabled: false },
                { connectionAuthType: 'UsernamePass', disabled: false },
            ]),
        );

        this.businessDomains$ = this.businessDomainService
            .listBusinessDomains({ returnAll: false })
            .pipe(tap((businessDomains) => this.businessDomains.set(businessDomains)));

        // Handle changes to connectionType and connectionAuthType
        this.createConnectionForm.get('connectionType')?.valueChanges.subscribe((connectionType) => {
            this.handleConnectionTypeChange(connectionType ?? 'DataLake');
        });

        this.createConnectionForm.get('connectionAuthType')?.valueChanges.subscribe((authType) => {
            const connectionType = this.createConnectionForm.get('connectionType')?.value ?? 'DataLake';
            this.handleAuthTypeChange(connectionType, authType);
        });

        // Initialize based on the current connectionType
        const initialType = this.createConnectionForm.get('connectionType')?.value ?? 'DataLake';
        this.handleConnectionTypeChange(initialType);
        this.prefillRetryForm();
    }

    public ngOnDestroy(): void {
        this.connectionCreationSubscription?.unsubscribe();
        this.stopElapsedTimeTracking();
        this.connectionResultModalRef?.close();
    }

    private handleConnectionTypeChange(connectionType: ConnectionType): void {
        // Reset fields when connection type changes
        this.createConnectionForm.patchValue({
            username: '',
            password: '',
            host: '',
            databaseName: '',
            port: null,
            isUnityCatalog: false,
            catalog: '',
        });
        this.updateCatalogState();

        if (connectionType === 'SQLServer') {
            this.disableNonSqlFields();
            this.enableSqlFields();
            this.setSqlValidators();
        } else if (connectionType === 'Databricks') {
            this.disableNonSqlFields();
            this.enableDatabricksFields();
            this.setDatabricksValidators();
        } else {
            this.enableNonSqlFields();
            this.disableSqlFields();
            this.setDataLakeValidators();
        }

        // Ensure auth type logic is applied when connection type changes
        const authType = this.createConnectionForm.get('connectionAuthType')?.value ?? 'ServicePrincipal';
        this.handleAuthTypeChange(connectionType, authType);
    }

    private prefillRetryForm(): void {
        const queryParams = this.route.snapshot.queryParamMap;
        const dataLakePath = queryParams.get('dataLakePath') ?? queryParams.get('host');
        if (!dataLakePath) {
            return;
        }

        const pathParts = /^(?:abfss?:\/{1,2})?([^@/]+)@([^/?#]+)(?:[/?#].*)?$/i.exec(dataLakePath.trim());
        if (!pathParts) {
            const container = queryParams.get('container');
            const datalake = queryParams.get('datalake');
            const dfsDomain = queryParams.get('dfsDomain');
            if (!container || !datalake || !dfsDomain) {
                this.toastrService.error('Could not prefill the Data Lake path for retry.', 'Retry unavailable');
                return;
            }

            this.patchRetryForm(queryParams.get('connectionAlias'), queryParams.get('businessDomainId'), {
                container,
                datalake,
                dfsDomain,
            });
            return;
        }

        const domainParts = pathParts[2].split('.');
        const datalake = domainParts[0];
        const dfsDomain = domainParts.length > 1 ? domainParts.slice(1).join('.') : 'dfs.core.windows.net';

        this.patchRetryForm(queryParams.get('connectionAlias'), queryParams.get('businessDomainId'), {
            container: pathParts[1],
            datalake,
            dfsDomain,
        });
    }

    private patchRetryForm(
        connectionAlias: string | null,
        businessDomainId: string | null,
        dataLakeFields: { container: string; datalake: string; dfsDomain: string },
    ): void {
        this.createConnectionForm.patchValue({
            connectionAlias: connectionAlias ?? '',
            businessDomainId: businessDomainId ?? '',
            connectionType: 'DataLake',
            connectionAuthType: 'ServicePrincipal',
            ...dataLakeFields,
            useBringYourOwnSP: false,
            clientId: '',
            clientSecret: '',
        });
    }

    private updateCatalogState(): void {
        const catalogControl = this.createConnectionForm.controls.catalog;
        const isCatalogRequired =
            this.createConnectionForm.controls.connectionType.value === 'Databricks' &&
            this.createConnectionForm.controls.isUnityCatalog.value;

        if (isCatalogRequired) {
            catalogControl.enable();
        } else {
            catalogControl.disable();
        }
    }

    private handleAuthTypeChange(connectionType: ConnectionType, authType: ConnectionAuthType): void {
        const usernameControl = this.createConnectionForm.get('username');

        if (connectionType === 'Databricks' && authType === 'UsernamePass') {
            usernameControl?.setValue('token');
        } else {
            usernameControl?.setValue(''); // Reset username for other cases
        }
    }

    protected createConnection(): void {
        const formValues = this.createConnectionForm.getRawValue();
        const initialType = this.createConnectionForm.get('connectionType')?.value ?? 'DataLake';

        if (this.createConnectionForm.invalid) {
            // Mark all fields as touched to show validation errors
            this.createConnectionForm.markAllAsTouched();
            this.toastrService.warning('Please complete all the required fields.', 'Missing information');
            return;
        }

        const usernameControl = this.createConnectionForm.get('username');
        const wasUsernameDisabled = usernameControl?.disabled;
        if (wasUsernameDisabled) {
            usernameControl.enable({ emitEvent: false });
        }

        this.isCreatingConnection.set(true);
        this.resultModalClosed = false;
        // Keep the already-open result modal in its pending state on retry instead of falling back to the success view.
        if (this.connectionResultModalRef) {
            this.provisioningState.set(ConnectionState.Pending);
            this.startElapsedTimeTracking();
        } else {
            this.provisioningState.set(null);
        }
        this.provisioningMessage.set(null);
        this.connectionResult.set({
            alias: formValues.connectionAlias,
            businessDomainId: formValues.businessDomainId,
            businessDomainName:
                this.businessDomains().find((businessDomain) => businessDomain.id === formValues.businessDomainId)
                    ?.name ?? formValues.businessDomainId,
            host: createConnectionHost(formValues, initialType),
            connectionType: initialType,
            authType: formValues.connectionAuthType,
            ...(initialType === 'Databricks' &&
                formValues.isUnityCatalog &&
                formValues.catalog.trim() && { catalog: formValues.catalog.trim() }),
        });

        this.connectionCreationSubscription = this.configService
            .getAppConfig()
            .pipe(
                switchMap(() => {
                    if (initialType === 'SQLServer') {
                        return this.http.post('connections/sql-database', createSqlConnectionRequest(formValues));
                    } else if (initialType === 'Databricks') {
                        return this.http.post('connections/databricks', createDatabricksConnectionRequest(formValues));
                    } else {
                        return this.connectionAutomationService
                            .createDataLakeConnection(createDataLakeConnectionRequest(formValues))
                            .pipe(
                                switchMap((response) => {
                                    if (response.status !== 202 || !response.body) {
                                        if (this.provisioningState() === ConnectionState.Pending) {
                                            this.provisioningState.set(ConnectionState.Failed);
                                            this.provisioningMessage.set('Connection provisioning failed.');
                                        }
                                        return of(undefined);
                                    }

                                    this.provisioningState.set(ConnectionState.Pending);
                                    this.startElapsedTimeTracking();
                                    this.openConnectionResultModal();
                                    return this.connectionPollingService.pollConnection(response.body.id).pipe(
                                        tap((connection) => {
                                            this.provisioningState.set(connection.state);
                                            this.provisioningMessage.set(connection.stateMessage);
                                        }),
                                        switchMap((connection) => {
                                            if (connection.state === ConnectionState.Failed) {
                                                throw new Error(
                                                    connection.stateMessage ?? 'Connection provisioning failed.',
                                                );
                                            }
                                            return of(connection);
                                        }),
                                        filter((connection) => connection.state === ConnectionState.Active),
                                        map(() => undefined),
                                    );
                                }),
                            );
                    }
                }),
                finalize(() => {
                    if (wasUsernameDisabled) {
                        usernameControl?.disable({ emitEvent: false });
                    }
                    this.isCreatingConnection.set(false);
                    this.stopElapsedTimeTracking();
                }),
                map((response) => response as { success: boolean; message?: string; requestPermissionUrl?: string }),
                map((response) => {
                    if (response && response.success === false) {
                        throw {
                            error: {
                                error: response.message || 'Connection creation failed.',
                                requestPermissionUrl: response.requestPermissionUrl,
                            },
                        };
                    }
                    return response;
                }),
                catchError((err: HttpErrorResponse | Error) => {
                    this.isCreatingConnection.set(false);
                    const errorMessage =
                        err instanceof HttpErrorResponse
                            ? (err.error?.message as string | undefined) || (err.error?.error as string | undefined)
                            : err.message;
                    if (this.provisioningState() === ConnectionState.Pending) {
                        this.provisioningState.set(ConnectionState.Failed);
                    }
                    this.provisioningMessage.set(errorMessage ?? 'Connection provisioning failed.');
                    if (
                        !(err instanceof HttpErrorResponse) &&
                        (this.resultModalClosed || !this.connectionResultModalRef)
                    ) {
                        this.toastrService.error(
                            errorMessage ?? 'An unexpected error occurred while creating the connection.',
                            'Connection creation failed',
                        );
                    }
                    return throwError(() => err);
                }),
                finalize(() => {
                    this.isCreatingConnection.set(false);
                }),
            )
            .subscribe({
                next: () => {
                    if (this.provisioningState() === ConnectionState.Active) {
                        if (this.resultModalClosed) {
                            this.toastrService.success('Your connection is ready to use.', 'Connection created');
                        } else {
                            this.openConnectionResultModal();
                        }
                    } else if (!this.provisioningState()) {
                        this.openConnectionResultModal();
                    }
                },
                error: () => {
                    // Error handling is now done in catchError above
                },
            });
    }

    protected closeResultModal(): void {
        this.resultModalClosed = true;
        this.connectionResultModalRef?.close();
    }

    private startElapsedTimeTracking(): void {
        this.stopElapsedTimeTracking();
        this.provisioningElapsedSeconds.set(0);
        this.elapsedTimeSubscription = interval(1000).subscribe((elapsedSeconds) =>
            this.provisioningElapsedSeconds.set(elapsedSeconds + 1),
        );
    }

    private stopElapsedTimeTracking(): void {
        this.elapsedTimeSubscription?.unsubscribe();
        this.elapsedTimeSubscription = undefined;
    }

    private openConnectionResultModal(): void {
        if (!this.connectionResultModalRef) {
            this.connectionResultModalRef = this.modalService.open(ConnectionResultModalComponent, {
                backdrop: 'static',
                keyboard: false,
                centered: true,
                size: 'lg',
            });
            const modal = this.connectionResultModalRef.componentInstance;
            modal.provisioningState = this.provisioningState;
            modal.provisioningMessage = this.provisioningMessage;
            modal.provisioningElapsedTime = (): string => this.getProvisioningElapsedTime();
            modal.connectionResult = this.connectionResult;
            modal.retry.subscribe((): void => this.createConnection());
            modal.closeRequested.subscribe((): void => this.closeResultModal());
            this.connectionResultModalRef.result.finally((): void => {
                this.resultModalClosed = true;
                this.connectionResultModalRef = undefined;
            });
        }
    }

    protected getProvisioningElapsedTime(): string {
        const elapsedSeconds = this.provisioningElapsedSeconds();
        const minutes = Math.floor(elapsedSeconds / 60);
        const seconds = elapsedSeconds % 60;
        return minutes === 0 ? `${seconds}s` : `${minutes}m ${seconds}s`;
    }

    private disableNonSqlFields(): void {
        const fields = ['alias', 'host', 'username', 'password', 'port', 'sourceName', 'roles'];
        fields.forEach((field) => this.createConnectionForm.get(field)?.disable());
        this.showInputForClientSecret.set(false);
    }

    private enableNonSqlFields(): void {
        const fields = ['alias', 'host', 'username', 'password', 'port', 'sourceName', 'isPushdown', 'roles'];
        fields.forEach((field) => this.createConnectionForm.get(field)?.enable());
        this.showInputForClientSecret.set(true);
    }

    private disableSqlFields(): void {
        const fields = ['host', 'password', 'username', 'databaseName', 'port'];
        fields.forEach((field) => this.createConnectionForm.get(field)?.disable());
        this.showInputForSQLPassword.set(false);
    }

    private enableSqlFields(): void {
        const fields = ['host', 'password', 'username', 'databaseName', 'port', 'isPushdown'];
        fields.forEach((field) => this.createConnectionForm.get(field)?.enable());
        this.showInputForSQLPassword.set(true);
    }

    private enableDatabricksFields(): void {
        const fields = ['host', 'password', 'username', 'port', 'isPushdown'];
        fields.forEach((field) => this.createConnectionForm.get(field)?.enable());
        this.createConnectionForm.get('databaseName')?.disable(); // Databricks doesn't use database name
        this.showInputForSQLPassword.set(true);

        this.createConnectionForm.get('connectionAuthType')?.valueChanges.subscribe((authType) => {
            const usernameControl = this.createConnectionForm.get('username');
            if (authType === 'UsernamePass') {
                usernameControl?.setValue('token');
            } else {
                usernameControl?.setValue('');
            }
        });
    }

    private setSqlValidators(): void {
        const sqlFields = ['host', 'password', 'username', 'databaseName', 'port'];
        sqlFields.forEach((field) => {
            this.createConnectionForm.get(field)?.setValidators([Validators.required]);
            this.createConnectionForm.get(field)?.updateValueAndValidity();
        });

        const dataLakeFields = ['container', 'datalake', 'dfsDomain'];
        dataLakeFields.forEach((field) => {
            this.createConnectionForm.get(field)?.clearValidators();
            this.createConnectionForm.get(field)?.updateValueAndValidity();
        });
    }

    private setDataLakeValidators(): void {
        const dataLakeFields = ['container', 'datalake', 'dfsDomain'];
        dataLakeFields.forEach((field) => {
            if (field === 'container') {
                this.createConnectionForm.get(field)?.setValidators([Validators.required, containerNameValidator()]);
            } else if (field === 'datalake') {
                this.createConnectionForm.get(field)?.setValidators([Validators.required, storageAccountValidator()]);
            } else if (field === 'dfsDomain') {
                this.createConnectionForm.get(field)?.setValidators([Validators.required, dfsDomainValidator()]);
            }
            this.createConnectionForm.get(field)?.updateValueAndValidity();
        });

        const sqlFields = ['host', 'password', 'username', 'databaseName', 'port'];
        sqlFields.forEach((field) => {
            this.createConnectionForm.get(field)?.clearValidators();
            this.createConnectionForm.get(field)?.updateValueAndValidity();
        });
    }

    private setDatabricksValidators(): void {
        const databricksFields = ['password', 'username', 'port'];
        databricksFields.forEach((field) => {
            this.createConnectionForm.get(field)?.setValidators([Validators.required]);
            this.createConnectionForm.get(field)?.updateValueAndValidity();
        });

        // Host (JDBC URL) has additional credential detection validator
        this.createConnectionForm.get('host')?.setValidators([Validators.required, jdbcUrlCredentialsValidator()]);
        this.createConnectionForm.get('host')?.updateValueAndValidity();

        this.createConnectionForm.get('databaseName')?.clearValidators();
        this.createConnectionForm.get('databaseName')?.updateValueAndValidity();
        this.createConnectionForm.get('container')?.clearValidators();
        this.createConnectionForm.get('container')?.updateValueAndValidity();
        this.createConnectionForm.get('datalake')?.clearValidators();
        this.createConnectionForm.get('datalake')?.updateValueAndValidity();
        this.createConnectionForm.get('dfsDomain')?.clearValidators();
        this.createConnectionForm.get('dfsDomain')?.updateValueAndValidity();
    }
}
